import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Web3ReactProvider } from '@web3-react/core';
import { ethers } from 'ethers';
import { ToastContainer } from 'react-toastify';

import { routes } from './routes';
import history from './history';
import BaseLayout from './scenes/BaseLayout/BaseLayout';
import store from './app/store';
import { createAppTheme } from './theme';
import PrivateRoute from './scenes/PrivateRoute/PrivateRoute';
import ScrollToTop from './scenes/ScrollToTop/ScrollToTop';
import { useEagerConnect } from './hooks/useEagerConnect';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';

import './fonts.css';
import 'react-toastify/dist/ReactToastify.css';
import 'animate.css/animate.min.css';

const POLLING_INTERVAL = 15000;

const getLibrary = (provider) => {
  const library = new ethers.providers.Web3Provider(provider);
  library.pollingInterval = POLLING_INTERVAL;
  return library;
};

const reload = () => window.location.reload();

const AppContent = () => {
  useEagerConnect();
  return (
    <BaseLayout history={history}>
      <Routes>
        {routes.map((route) => {
          if (route.isProtected)
            return (
              <Route key={route.path} path={route.path} element={<PrivateRoute />}>
                <Route path={route.path} exact={route.exact} element={route.component} />
              </Route>
            );
          return (
            <Route key={route.path} path={route.path} exact={route.exact} element={route.component} />
          );
        })}
        <Route path="/TermsAndConditions.html" onEnter={reload} />
      </Routes>
    </BaseLayout>
  );
};

// Reads mode from context and builds the theme dynamically
const ThemedApp = () => {
  const { mode } = useThemeMode();
  const theme = createAppTheme(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={mode}
        toastStyle={{
          backgroundColor: mode === 'dark' ? '#0a1019' : '#ffffff',
          border: mode === 'dark'
            ? '1px solid rgba(210,157,92,0.3)'
            : '1px solid rgba(200,144,74,0.2)',
          color: mode === 'dark' ? '#fff' : '#0a1020',
          fontFamily: '"Space Grotesk", sans-serif',
        }}
      />

      <AppContent />
    </ThemeProvider>
  );
};

const App = () => (
  <Web3ReactProvider getLibrary={getLibrary}>
    <Provider store={store}>
      <ThemeModeProvider>
        <ThemedApp />
      </ThemeModeProvider>
    </Provider>
  </Web3ReactProvider>
);

export default App;
